import api from './api';

export interface CreatorApplicationRequest {
  requestedLevel: 'CREATOR_20' | 'CREATOR_30';
  artworkFiles?: string[];
  artworkLinks?: string[];
  socialLinks?: string[];
  websiteLinks?: string[];
  communicationMethod?: string;
  comment?: string;
}

export interface DiscountInfo {
  id?: string;
  type: string;
  percent: number;
  source?: string;
  appliedAt?: string;
  expiresAt?: string | null;
  affiliateCode?: string | null;
  shouldNotify?: boolean;
}

export interface PromotionPreview {
  codes: string[];
  itemsSubtotalUsd: number;
  shippingAmountUsd: number;
  promotionDiscountTotalUsd: number;
  totalAfterPromotionsUsd: number;
  redemptions: Array<{
    promotionId: string;
    code: string;
    name: string;
    discountAmountUsd: number;
  }>;
}

export interface AffiliateInfo {
  code: string;
  referralLink: string;
}

export interface AffiliateStats {
  totals: {
    registeredUsers: number;
    printedItems: number;
    moneyEarned: number;
  };
  rows: Array<{
    date: string;
    registeredUsers: number;
    printedItems: number;
    moneyEarned: number;
  }>;
}

export interface CreatorApplicationResponse {
  application: {
    id: string;
    requestedLevel: string;
    status: string;
    createdAt: string;
    decidedAt: string | null;
    adminNotes: string | null;
  } | null;
  currentDiscount: DiscountInfo | null;
}

export const discountApi = {
  // Creator applications
  async submitCreatorApplication(data: CreatorApplicationRequest): Promise<void> {
    await api.post('/creator-applications', data);
  },

  async getMyCreatorApplication(): Promise<CreatorApplicationResponse> {
    const response = await api.get('/creator-applications/me');
    return response.data;
  },

  // User discount
  async getMyDiscount(): Promise<DiscountInfo | null> {
    const response = await api.get('/user/discount');
    return response.data;
  },

  async acknowledgeDiscountNotification(): Promise<{ acknowledged: boolean }> {
    const response = await api.post('/user/discount/acknowledge');
    return response.data;
  },

  // Affiliate
  async getAffiliateInfo(): Promise<AffiliateInfo | null> {
    const response = await api.get('/user/affiliate-info');
    return response.data;
  },

  async getAffiliateStats(): Promise<AffiliateStats> {
    const response = await api.get('/user/affiliate-stats');
    return response.data;
  },

  // Promo codes - using the backend validate endpoint
  async validatePromoCode(code: string): Promise<{ valid: boolean; discount?: number }> {
    const response = await api.get(`/affiliate/validate?code=${code}`);
    return response.data;
  },

  // Apply referral code
  async applyReferralCode(code: string): Promise<{ success: boolean; message: string; alreadyHadDiscount?: boolean }> {
    const response = await api.post('/user/apply-referral', { code });
    return response.data;
  },

  async previewPromotionCodes(orderId: string, promoCodes: string[]): Promise<PromotionPreview> {
    const response = await api.post<{ data: PromotionPreview }>('/promotions/preview', {
      orderId,
      promoCodes,
    });
    return response.data.data;
  },

  // Apply initial discount (no code needed)
  async applyInitialDiscount(): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/user/apply-initial-discount');
    return response.data;
  },

  // Referral check (alias for validate)
  async checkReferral(code: string): Promise<{ valid: boolean }> {
    const response = await api.get(`/affiliate/validate?code=${code}`);
    return response.data;
  },
};
